import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import '../../styles/Table.css';

const LocationsListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    fetchLocations();
  }, [searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 500);
  };

  const fetchLocations = async () => {
    try {
      const params = {};
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const res = await api.getLocations(params);
      if (res.success) {
        const activeElement = document.activeElement;
        const wasSearchFocused = activeElement === searchInputRef.current;
        setLocations(res.data || []);
        if (wasSearchFocused && searchInputRef.current) {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Error fetching locations');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteLocation(id);
      toast.success('Location deleted successfully');
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Error deleting location');
    }
  };

  if (loading && locations.length === 0) {
    return <Loader />;
  }

  return (
    <div className="locations-list-page" style={{ padding: '16px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ flex: 1, marginRight: '12px' }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search locations by name..."
            defaultValue={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          />
        </div>
        <button
          onClick={() => navigate('/admin/locations/add')}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>+</span>
          <span>Add Location</span>
        </button>
      </div>

      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {locations.length === 0 && !loading ? (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#718096',
              fontSize: '14px',
            }}
          >
            No locations found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Location Name
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                      width: '100px',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#718096',
                      width: '120px',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => {
                  const id = location._id || location.id;
                  return (
                    <tr key={id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px', color: '#2d3748', fontWeight: 600, fontSize: '13px' }}>
                        {location.name}
                      </td>
                      <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                        {location.description || 'N/A'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: location.isActive ? '#d1fae5' : '#fee2e2',
                            color: location.isActive ? '#065f46' : '#991b1b',
                          }}
                        >
                          {location.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={() => navigate(`/admin/locations/${id}/edit`)}
                            style={{
                              padding: '6px 10px',
                              background: '#e6fffa',
                              color: '#234e52',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteTarget(id)}
                            style={{
                              padding: '6px 10px',
                              background: '#fed7d7',
                              color: '#c53030',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 500,
                            }}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            handleDelete(deleteTarget);
            setDeleteTarget(null);
          }
        }}
        title="Delete Location"
        message="Are you sure you want to delete this location? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default LocationsListPage;

